import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Users, 
  TrendingUp, 
  BarChart3, 
  LogOut,
  Calendar,
  Mail,
  Building2,
  FileDown,
  Target,
  ArrowUpRight,
  ArrowDownRight
} from "lucide-react";
import { format, subDays, isAfter } from "date-fns";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

interface Lead {
  id: string;
  first_name: string;
  email: string;
  business_name: string | null;
  source: string;
  industry: string | null;
  created_at: string;
  converted: boolean;
}

interface SourceStats {
  source: string;
  count: number;
  label: string;
  icon: string;
  color: string;
  thisWeek: number;
  lastWeek: number;
}

// Updated source labels to match new tracking routes
const sourceConfig: Record<string, { label: string; icon: string; color: string }> = {
  // New source tracking
  organic: { label: "Direct / Organic", icon: "🌐", color: "#22c55e" },
  facebook: { label: "Facebook", icon: "📘", color: "#1877F2" },
  instagram: { label: "Instagram", icon: "📸", color: "#E4405F" },
  linkedin: { label: "LinkedIn", icon: "💼", color: "#0A66C2" },
  twitter: { label: "Twitter/X", icon: "🐦", color: "#1DA1F2" },
  youtube: { label: "YouTube", icon: "🎬", color: "#FF0000" },
  "google-ads": { label: "Google Ads", icon: "🎯", color: "#4285F4" },
  email: { label: "Email Campaign", icon: "📧", color: "#F59E0B" },
  referral: { label: "Referral", icon: "🤝", color: "#8B5CF6" },
  pdf: { label: "PDF Download", icon: "📄", color: "#EC4899" },
  // Legacy source codes
  a: { label: "Direct / Organic (Legacy)", icon: "🌐", color: "#22c55e" },
  b: { label: "LinkedIn (Legacy)", icon: "💼", color: "#0A66C2" },
  c: { label: "Facebook (Legacy)", icon: "📘", color: "#1877F2" },
  d: { label: "Instagram (Legacy)", icon: "📸", color: "#E4405F" },
};

const CHART_COLORS = ['#B88C3C', '#192335', '#22c55e', '#1877F2', '#E4405F', '#0A66C2', '#FF0000', '#F59E0B', '#8B5CF6'];

const LeadsAnalytics = () => {
  const navigate = useNavigate();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      setUser(session.user);
      fetchLeads();
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (!session) {
        navigate("/auth");
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchLeads = async () => {
    try {
      const response = await fetch(
        `https://nzjyvoutgeltljazheej.supabase.co/rest/v1/growth_plan_leads?select=*&order=created_at.desc`,
        {
          headers: {
            'apikey': 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Im56anl2b3V0Z2VsdGxqYXpoZWVqIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTY5MTk1MzIsImV4cCI6MjA3MjQ5NTUzMn0.q5fVU_UZ5gLs1GY3gYWqd2Cww-_gDYYQpg28n1v6uYo',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error("Error fetching leads:", errorText);
        if (response.status === 404 || errorText.includes("relation") || errorText.includes("does not exist")) {
          setError("Table not found. Please run the database migration first.");
        } else {
          setError("Failed to fetch leads");
        }
        return;
      }
      
      const data = await response.json();
      setLeads(data || []);
    } catch (err) {
      console.error("Error fetching leads:", err);
      setError("Failed to fetch leads");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  const getSourceStats = (): SourceStats[] => {
    const stats: Record<string, { count: number; thisWeek: number; lastWeek: number }> = {};
    const now = new Date();
    const weekAgo = subDays(now, 7);
    const twoWeeksAgo = subDays(now, 14);
    
    leads.forEach((lead) => {
      const leadDate = new Date(lead.created_at);
      if (!stats[lead.source]) {
        stats[lead.source] = { count: 0, thisWeek: 0, lastWeek: 0 };
      }
      stats[lead.source].count++;
      
      if (isAfter(leadDate, weekAgo)) {
        stats[lead.source].thisWeek++;
      } else if (isAfter(leadDate, twoWeeksAgo)) {
        stats[lead.source].lastWeek++;
      }
    });
    
    return Object.entries(stats)
      .map(([source, data]) => ({
        source,
        count: data.count,
        thisWeek: data.thisWeek,
        lastWeek: data.lastWeek,
        label: sourceConfig[source]?.label || source,
        icon: sourceConfig[source]?.icon || "📊",
        color: sourceConfig[source]?.color || "#64748b",
      }))
      .sort((a, b) => b.count - a.count);
  };

  const getWeeklyTrend = () => {
    const days: Record<string, number> = {};
    for (let i = 6; i >= 0; i--) {
      const date = format(subDays(new Date(), i), 'MMM d');
      days[date] = 0;
    }
    
    leads.forEach((lead) => {
      const leadDate = new Date(lead.created_at);
      if (isAfter(leadDate, subDays(new Date(), 7))) {
        const dateKey = format(leadDate, 'MMM d');
        if (days[dateKey] !== undefined) {
          days[dateKey]++;
        }
      }
    });
    
    return Object.entries(days).map(([date, count]) => ({ date, leads: count }));
  };

  const getIndustryBreakdown = () => {
    const industries: Record<string, number> = {};
    leads.forEach((lead) => {
      const industry = lead.industry || 'Unknown';
      industries[industry] = (industries[industry] || 0) + 1;
    });
    return Object.entries(industries)
      .map(([name, value]) => ({ name, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 8);
  };

  const sourceStats = getSourceStats();
  const weeklyTrend = getWeeklyTrend();
  const industryBreakdown = getIndustryBreakdown();
  const totalLeads = leads.length;
  const todayLeads = leads.filter(
    (lead) => new Date(lead.created_at).toDateString() === new Date().toDateString()
  ).length;
  const thisWeekLeads = leads.filter(
    (lead) => isAfter(new Date(lead.created_at), subDays(new Date(), 7))
  ).length;
  const lastWeekLeads = leads.filter(
    (lead) => {
      const date = new Date(lead.created_at);
      return isAfter(date, subDays(new Date(), 14)) && !isAfter(date, subDays(new Date(), 7));
    }
  ).length;
  const weekOverWeekChange = lastWeekLeads > 0 
    ? ((thisWeekLeads - lastWeekLeads) / lastWeekLeads * 100).toFixed(1) 
    : thisWeekLeads > 0 ? "100" : "0";

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-primary text-white py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Lead Source Analytics</h1>
            <p className="text-white/70 text-sm">Track conversions by traffic source</p>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-white/70 text-sm">{user?.email}</span>
            <Button variant="ghost" size="sm" onClick={handleLogout} className="text-white hover:bg-white/10">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-6">
        {error && (
          <Card className="mb-8 border-destructive">
            <CardContent className="pt-6">
              <p className="text-destructive">{error}</p>
              <p className="text-sm text-muted-foreground mt-2">
                The growth_plan_leads table needs to be created. Please check your Supabase database connection.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Overview Stats */}
        <div className="grid md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Leads</CardTitle>
              <Users className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{totalLeads}</p>
              <p className="text-xs text-muted-foreground">All time</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Today</CardTitle>
              <Calendar className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{todayLeads}</p>
              <p className="text-xs text-muted-foreground">New leads today</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">This Week</CardTitle>
              <TrendingUp className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{thisWeekLeads}</p>
              <div className={`flex items-center text-xs ${Number(weekOverWeekChange) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {Number(weekOverWeekChange) >= 0 ? (
                  <ArrowUpRight className="w-3 h-3 mr-1" />
                ) : (
                  <ArrowDownRight className="w-3 h-3 mr-1" />
                )}
                {weekOverWeekChange}% vs last week
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Top Source</CardTitle>
              <Target className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-xl font-bold truncate">
                {sourceStats.length > 0 ? sourceStats[0]?.label : "N/A"}
              </p>
              <p className="text-xs text-muted-foreground">
                {sourceStats.length > 0 ? `${sourceStats[0]?.count} leads` : "—"}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Active Sources</CardTitle>
              <BarChart3 className="w-4 h-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold">{sourceStats.length}</p>
              <p className="text-xs text-muted-foreground">Traffic channels</p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Weekly Trend */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">7-Day Lead Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={weeklyTrend}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis dataKey="date" className="text-xs" />
                    <YAxis className="text-xs" />
                    <Tooltip />
                    <Line 
                      type="monotone" 
                      dataKey="leads" 
                      stroke="#B88C3C" 
                      strokeWidth={2}
                      dot={{ fill: '#B88C3C' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Source Distribution Pie */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Lead Source Distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={sourceStats.map(s => ({ name: s.label, value: s.count }))}
                      cx="50%"
                      cy="50%"
                      innerRadius={40}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {sourceStats.map((entry, index) => (
                        <Cell key={entry.source} fill={entry.color || CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="flex flex-wrap gap-2 mt-4 justify-center">
                {sourceStats.slice(0, 5).map((stat) => (
                  <div key={stat.source} className="flex items-center gap-1 text-xs">
                    <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stat.color }} />
                    <span>{stat.label}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Source Performance Cards */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Performance by Source</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sourceStats.map((stat) => {
                const percentage = totalLeads > 0 ? ((stat.count / totalLeads) * 100).toFixed(1) : 0;
                const trend = stat.lastWeek > 0 
                  ? ((stat.thisWeek - stat.lastWeek) / stat.lastWeek * 100).toFixed(0)
                  : stat.thisWeek > 0 ? "100" : "0";
                
                return (
                  <div 
                    key={stat.source} 
                    className="p-4 rounded-lg border bg-card hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{stat.icon}</span>
                        <span className="font-medium text-sm">{stat.label}</span>
                      </div>
                    </div>
                    <p className="text-3xl font-bold mb-1">{stat.count}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-muted-foreground">{percentage}% of total</span>
                      {stat.thisWeek > 0 && (
                        <span className={`text-xs flex items-center ${Number(trend) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                          {Number(trend) >= 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
                          {trend}%
                        </span>
                      )}
                    </div>
                    <div className="mt-2 h-2 bg-muted rounded-full overflow-hidden">
                      <div 
                        className="h-full rounded-full transition-all"
                        style={{ 
                          width: `${percentage}%`,
                          backgroundColor: stat.color 
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Industry Breakdown */}
        {industryBreakdown.length > 0 && (
          <Card className="mb-8">
            <CardHeader>
              <CardTitle>Leads by Industry</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={industryBreakdown} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis type="number" className="text-xs" />
                    <YAxis dataKey="name" type="category" className="text-xs" width={120} />
                    <Tooltip />
                    <Bar dataKey="value" fill="#192335" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Tracking URLs Reference */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileDown className="w-5 h-5" />
              Tracking URLs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-4">
              Use these URLs to track leads from different traffic sources:
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {[
                { path: "/do-you-qualify", label: "Main Funnel", source: "organic" },
                { path: "/fb", label: "Facebook", source: "facebook" },
                { path: "/ig", label: "Instagram", source: "instagram" },
                { path: "/li", label: "LinkedIn", source: "linkedin" },
                { path: "/tw", label: "Twitter/X", source: "twitter" },
                { path: "/yt", label: "YouTube", source: "youtube" },
                { path: "/gads", label: "Google Ads", source: "google-ads" },
                { path: "/email", label: "Email Campaign", source: "email" },
                { path: "/ref", label: "Referral", source: "referral" },
              ].map((url) => (
                <div key={url.path} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                  <div>
                    <span className="font-mono text-sm">{url.path}</span>
                    <p className="text-xs text-muted-foreground">{url.label}</p>
                  </div>
                  <Badge variant="secondary">{url.source}</Badge>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-accent/50 rounded-lg">
              <p className="text-sm font-medium mb-1">📄 PDF Download Tracking</p>
              <p className="text-xs text-muted-foreground">
                PDFs include UTM parameters: <code className="bg-muted px-1 rounded">utm_source=pdf&utm_medium=growth-plan</code>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Leads Table */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Leads</CardTitle>
          </CardHeader>
          <CardContent>
            {leads.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Users className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No leads yet. Share your growth plan pages to start collecting leads.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Business</TableHead>
                    <TableHead>Industry</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Date</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.slice(0, 50).map((lead) => {
                    const config = sourceConfig[lead.source];
                    return (
                      <TableRow key={lead.id}>
                        <TableCell className="font-medium">{lead.first_name}</TableCell>
                        <TableCell>
                          <a 
                            href={`mailto:${lead.email}`} 
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <Mail className="w-3 h-3" />
                            {lead.email}
                          </a>
                        </TableCell>
                        <TableCell>
                          {lead.business_name ? (
                            <span className="flex items-center gap-1">
                              <Building2 className="w-3 h-3 text-muted-foreground" />
                              {lead.business_name}
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          {lead.industry ? (
                            <Badge variant="outline">{lead.industry}</Badge>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="secondary"
                            className="flex items-center gap-1 w-fit"
                            style={{ borderLeft: `3px solid ${config?.color || '#64748b'}` }}
                          >
                            <span>{config?.icon || "📊"}</span>
                            {config?.label || lead.source}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {format(new Date(lead.created_at), "MMM d, yyyy HH:mm")}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default LeadsAnalytics;